import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AllocatePromptsDialog.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Slider } from 'primereact/slider';
import { ProgressBar } from 'primereact/progressbar';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { PRICING } from '../../../../shared/routes/path';
import { numberFormat } from '../../../../shared/utils/utils';
import type { AgencyHubProject } from '../../agencyHubRows';

/** One project's draft: its allocation (null = no limit) and whether it runs after Save. */
export interface AllocationDraft {
  allocation: number | null;
  active: boolean;
}

export type AllocationDrafts = Record<number, AllocationDraft>;

interface AllocatePromptsDialogProps {
  visible: boolean;
  projects: AgencyHubProject[];
  pool: number;
  /** Row to scroll to and highlight — the per-project entry from the table. */
  focusProjectId: number | null;
  onHide: () => void;
  onSave: (drafts: AllocationDrafts) => void;
}

const initialDrafts = (projects: AgencyHubProject[]): AllocationDrafts =>
  Object.fromEntries(
    projects.map((project) => [
      project.id,
      { allocation: project.allocation, active: project.status === 'ACTIVE' },
    ])
  );

/** The smallest allocation a running project can hold: what it uses, plus what a start would resume. */
const minAllocation = (project: AgencyHubProject, draft: AllocationDraft): number =>
  draft.active ? project.used + (project.status === 'ACTIVE' ? 0 : project.pausedPrompts) : 0;

/** What the draft takes from the pool: its allocation, or — with no limit — just what it uses. */
const committedOf = (project: AgencyHubProject, draft: AllocationDraft): number =>
  draft.allocation ?? minAllocation(project, draft);

/**
 * Pause releases the allocation; start reserves at least what the project resumes. A toggle BACK to
 * the state the dialog opened with restores the opening allocation — otherwise a pause-then-start
 * round trip would leave an active project at its minimum, and a manually paused one at 0.
 */
const toggledDraft = (project: AgencyHubProject, draft: AllocationDraft): AllocationDraft => {
  const next: AllocationDraft = { ...draft, active: !draft.active };
  const backToOpening = next.active === (project.status === 'ACTIVE');
  if (backToOpening) {
    next.allocation = project.allocation;
  } else {
    // A start reserves what it resumes; the paused draft holds 0, so the floor decides.
    next.allocation = next.active ? Math.max(minAllocation(project, next), draft.allocation ?? 0) : 0;
  }
  return next;
};

/**
 * "Allocate prompts": every project's allocation edited together and saved as ONE change, so cutting
 * one project to feed another is a single move. Pause and start live here too (the control between
 * the slider and the number input), because only together with them can the pool really be moved:
 * a paused project is greyed out and holds 0, starting it reserves at least the prompts it resumes.
 * Everything is a draft until Save; Save is blocked while the sum exceeds the pool or nothing changed.
 * A project may have no limit (empty input): it takes only the prompts it uses from the pool.
 * Rows are sorted by name, case- and accent-insensitively (decision 2026-10-19).
 */
const AllocatePromptsDialog: React.FC<AllocatePromptsDialogProps> = ({
  visible,
  projects,
  pool,
  focusProjectId,
  onHide,
  onSave,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<AllocationDrafts>({});
  const rowRefs = useRef(new Map<number, HTMLDivElement>());

  // Fresh draft on every open; the dialog never writes back until Save.
  useEffect(() => {
    if (visible) {
      setDrafts(initialDrafts(projects));
    }
  }, [visible, projects]);

  useEffect(() => {
    if (visible && focusProjectId !== null) {
      // Let the dialog mount its content before scrolling the list.
      const id = window.setTimeout(
        () => rowRefs.current.get(focusProjectId)?.scrollIntoView({ block: 'center' }),
        0
      );
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [visible, focusProjectId]);

  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })
      ),
    [projects]
  );

  const draftOf = (project: AgencyHubProject): AllocationDraft =>
    drafts[project.id] ?? { allocation: project.allocation, active: project.status === 'ACTIVE' };

  const setAllocation = (project: AgencyHubProject, value: number | null | undefined) =>
    setDrafts((prev) => {
      const draft = prev[project.id] ?? draftOf(project);
      const min = minAllocation(project, draft);
      // A cleared input removes the limit.
      const allocation = value === null || value === undefined ? null : Math.max(min, Math.min(pool, value));
      return { ...prev, [project.id]: { ...draft, allocation } };
    });

  const toggleActive = (project: AgencyHubProject) =>
    setDrafts((prev) => {
      const draft = prev[project.id] ?? draftOf(project);
      return { ...prev, [project.id]: toggledDraft(project, draft) };
    });

  const allocated = useMemo(
    () =>
      projects.reduce(
        (sum, project) => (drafts[project.id] ? sum + committedOf(project, drafts[project.id]) : sum),
        0
      ),
    [drafts, projects]
  );
  const over = allocated > pool;
  const left = pool - allocated;
  const changed = useMemo(() => {
    const initial = initialDrafts(projects);
    return projects.some(
      (project) =>
        drafts[project.id] !== undefined &&
        (drafts[project.id].allocation !== initial[project.id].allocation ||
          drafts[project.id].active !== initial[project.id].active)
    );
  }, [drafts, projects]);

  const footer = (
    <>
      <Button className="btn btn--secondary" label={t('cancel')} onClick={onHide} />
      <Button
        className="btn btn--primary"
        label={t('save')}
        disabled={over || !changed}
        onClick={() => {
          onSave(drafts);
          onHide();
        }}
      />
    </>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t('allocatePrompts')}
      footer={footer}
      className="dialog-box allocate-prompts-dialog"
      draggable={false}
    >
      <p className="allocate-prompts-dialog__subtitle">{t('allocatePromptsSubtitle')}</p>

      <div className="allocate-prompts-dialog__summary">
        <div className="allocate-prompts-dialog__summary-row">
          <span>{t('allocated')}</span>
          <span className={over ? 'allocate-prompts-dialog__total--over' : ''}>
            {numberFormat().format(allocated)} / {numberFormat().format(pool)}
          </span>
        </div>
        <ProgressBar
          className={`progress-bar progress-bar--mini ${
            over ? 'progress-bar--danger' : 'progress-bar--primary'
          }`}
          value={Math.min(100, (allocated / pool) * 100)}
          showValue={false}
        />
        <div
          className={`allocate-prompts-dialog__hint${
            over ? ' allocate-prompts-dialog__hint--over' : ''
          }`}
        >
          {over
            ? t('promptsOverLimit', { count: allocated - pool })
            : t('promptsLeftInPool', { count: left })}
        </div>
      </div>

      <div className="allocate-prompts-dialog__list">
        {sortedProjects.map((project) => {
          const draft = draftOf(project);
          const min = minAllocation(project, draft);
          // A paused draft shows what a start would reserve, and the start is blocked while the pool's
          // free rest (in the current draft, so freeing room elsewhere first unblocks it) cannot cover it.
          const started = draft.active ? null : toggledDraft(project, draft);
          const startNeeds = started ? committedOf(project, started) : 0;
          // The label matches the table: a PAUSED project shows what Start resumes; a pause made only in
          // this draft previews what it would park (the prompts in use).
          const resumes = project.status === 'PAUSED' ? project.pausedPrompts : project.used;
          const startExtra = started ? startNeeds - committedOf(project, draft) : 0;
          const startShortBy = startExtra > 0 ? startExtra - Math.max(0, left) : 0;
          const rowClass = [
            'allocate-prompts-dialog__row',
            project.id === focusProjectId ? 'allocate-prompts-dialog__row--focused' : '',
            !draft.active ? 'allocate-prompts-dialog__row--paused' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={project.id}
              ref={(el) => {
                if (el) rowRefs.current.set(project.id, el);
                else rowRefs.current.delete(project.id);
              }}
              className={rowClass}
            >
              <div className="allocate-prompts-dialog__project">
                <span className="allocate-prompts-dialog__name">{project.name}</span>
                {draft.active && (
                  <span className="allocate-prompts-dialog__used">{t('minUsed', { count: min })}</span>
                )}
              </div>
              <div className="allocate-prompts-dialog__controls">
                {draft.active ? (
                  <Slider
                    className="allocate-prompts-dialog__slider"
                    value={draft.allocation ?? min}
                    min={min}
                    max={pool}
                    step={1}
                    onChange={(e) => setAllocation(project, e.value as number)}
                  />
                ) : (
                  <span className="allocate-prompts-dialog__on-start">
                    {/* The prompts a start resumes, worded as in the table's Prompts column. */}
                    {resumes > 0
                      ? t('agencyHubPausedPrompts', { count: resumes })
                      : t('agencyHubPausedNoPrompts')}
                  </span>
                )}
                {/* Same pause/start control as the table row and the bulk panel; a draft until Save. The
                    tooltip targets a wrapper: a disabled button fires no mouse events. */}
                <span id={`allocate-toggle-${project.id}`}>
                  <Button
                    className="action-btn"
                    disabled={startShortBy > 0}
                    onClick={() => toggleActive(project)}
                  >
                    <span className={`${draft.active ? 'ico--pause' : 'ico--play'} ico--xs`} />
                  </Button>
                </span>
                <CustomTooltip
                  tooltipText={
                    draft.active
                      ? 'pauseProject'
                      : startShortBy > 0
                      ? 'allocateStartPoolShort'
                      : 'startProject'
                  }
                  options={startShortBy > 0 ? { count: startShortBy } : undefined}
                  targetRef={`allocate-toggle-${project.id}`}
                />
                {/* A paused row keeps the input's width, so the pause/start control stays in one column. */}
                {!draft.active && <span className="allocate-prompts-dialog__input-spacer" />}
                {draft.active && (
                  <InputNumber
                    className="allocate-prompts-dialog__input"
                    value={draft.allocation}
                    min={min}
                    max={pool}
                    useGrouping={false}
                    placeholder={t('promptAllocationNoLimit')}
                    onValueChange={(e) => setAllocation(project, e.value)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {over && (
        <Button
          className="btn btn--link allocate-prompts-dialog__promo"
          label={t('needMorePrompts')}
          onClick={() => navigate(PRICING)}
        />
      )}
    </Dialog>
  );
};

export default AllocatePromptsDialog;
